import React, { useEffect, useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { Printer, Download, ArrowLeft, Store, Copy, Check, ExternalLink, Layers, FileText } from 'lucide-react';
import QRCode from 'qrcode';
function getMenuUrl(slug: string): string {
  return `https://noizacr.com/r/${slug}`;
}
export function MenuQRPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [quantity, setQuantity] = useState<number>(50);
  const [perPage, setPerPage] = useState<number>(12);
  const menuUrl = restaurant ? getMenuUrl(restaurant.slug) : '';
  useEffect(() => {
    if (!restaurant) return;
    let cancelled = false;
    async function generate() {
      try {
        setLoading(true);
        const dataUrl = await QRCode.toDataURL(menuUrl, {
          width: 600,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          },
          errorCorrectionLevel: 'H'
        });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) {
        console.error('Error generating menu QR:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    generate();
    return () => {
      cancelled = true;
    };
  }, [restaurant, menuUrl]);
  const handleDownload = () => {
    if (!qrDataUrl || !restaurant) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `Menu-QR-${restaurant.slug}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(menuUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };
  const handlePrint = () => {
    if (!qrDataUrl || !restaurant) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printCss = ['body { font-family: system-ui, -apple-system, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; margin: 0; background: #f3f4f6; }', '.qr-card { background: white; padding: 2.5rem; border-radius: 1rem; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); text-align: center; max-width: 480px; width: 100%; }', '.logo { width: 96px; height: 96px; border-radius: 1rem; object-fit: cover; margin-bottom: 1rem; }', '.restaurant-name { font-size: 1.5rem; font-weight: bold; color: #111827; margin: 0 0 0.5rem 0; }', '.title { font-size: 2rem; font-weight: 900; color: #000; margin: 0 0 1.5rem 0; }', '.qr-image { width: 100%; max-width: 360px; height: auto; margin: 0 auto 1.5rem auto; display: block; }', '.instruction { font-size: 1.25rem; font-weight: 700; color: #111827; margin: 0 0 0.5rem 0; }', '.subtitle { font-size: 0.95rem; color: #6b7280; margin: 0; }', '@media print { body { background: white; align-items: flex-start; padding-top: 2rem; } .qr-card { box-shadow: none; border: 2px dashed #e5e7eb; } }'].join('\n');
    const styleTag = '<' + 'style>' + printCss + '<' + '/style>';
    const scriptBody = 'window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };';
    const scriptTag = '<' + 'script>' + scriptBody + '<' + '/script>';
    const logoHtml = restaurant.logo_url ? '<img src="' + restaurant.logo_url + '" alt="Logo" class="logo" />' : '';
    const html = '<!DOCTYPE html><html><head><title>QR Menú - ' + restaurant.name + '</title>' + styleTag + '</head><body><div class="qr-card">' + logoHtml + '<h2 class="restaurant-name">' + restaurant.name + '</h2>' + '<h1 class="title">Nuestro menú digital</h1>' + '<img src="' + qrDataUrl + '" alt="QR Menú" class="qr-image" />' + '<p class="instruction">Escaneá para ver el menú</p>' + '<p class="subtitle">Autoatención rápida y segura</p>' + '</div>' + scriptTag + '</body></html>';
    printWindow.document.write(html);
    printWindow.document.close();
  };
  const handlePrintSheet = () => {
    if (!qrDataUrl || !restaurant) return;
    const safeQty = Math.max(1, Math.min(500, Math.floor(quantity || 1)));
    const cols = perPage === 6 ? 2 : perPage === 12 ? 3 : 4;
    const rows = perPage === 6 ? 3 : perPage === 12 ? 4 : 6;
    const totalPages = Math.ceil(safeQty / perPage);
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const css = ['@page { size: A4; margin: 8mm; }', 'body { font-family: system-ui, -apple-system, sans-serif; margin: 0; padding: 0; background: #f3f4f6; }', '.page { width: 100%; box-sizing: border-box; padding: 4mm; display: grid; grid-template-columns: repeat(' + cols + ', 1fr); grid-template-rows: repeat(' + rows + ', 1fr); gap: 4mm; page-break-after: always; }', '.page:last-child { page-break-after: auto; }', '.tile { border: 1px dashed #cbd5e1; border-radius: 8px; padding: 4mm 3mm; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; text-align: center; background: white; box-sizing: border-box; overflow: hidden; }', '.tile .logo { width: 12mm; height: 12mm; border-radius: 3mm; object-fit: cover; margin: 0 0 2mm 0; border: 1px solid #f3f4f6; }', '.tile .name { font-size: 7pt; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 1mm 0; line-height: 1.2; }', '.tile .title { font-size: 11pt; font-weight: 900; color: #111827; margin: 0 0 2mm 0; line-height: 1.1; }', '.tile .qr { flex: 1 1 auto; display: flex; align-items: center; justify-content: center; width: 100%; min-height: 0; margin: 1mm 0; }', '.tile .qr img { width: 100%; max-width: 90%; height: auto; max-height: 100%; object-fit: contain; }', '.tile .cta { font-size: 8pt; font-weight: 700; color: #111827; margin: 2mm 0 0.5mm 0; }', '.tile .sub { font-size: 6pt; font-weight: 500; color: #9ca3af; margin: 0; }', '.toolbar { padding: 12px 16px; background: white; position: sticky; top: 0; display: flex; gap: 12px; align-items: center; border-bottom: 1px solid #e5e7eb; z-index: 10; }', '.toolbar button { padding: 8px 16px; border-radius: 8px; border: 0; background: #111827; color: white; font-weight: 600; cursor: pointer; font-size: 14px; }', '.toolbar .info { color: #6b7280; font-size: 13px; }', '@media print { .toolbar { display: none; } body { background: white; } .page { padding: 0; } }'].join('\n');
    const logoHtml = restaurant.logo_url ? '<img src="' + restaurant.logo_url + '" alt="Logo" class="logo" />' : '';
    const tileHtml = '<div class="tile">' + logoHtml + '<div class="name">' + restaurant.name + '</div>' + '<div class="title">Nuestro menú digital</div>' + '<div class="qr"><img src="' + qrDataUrl + '" alt="QR" /></div>' + '<div class="cta">Escaneá para ver el menú</div>' + '<div class="sub">Autoatención rápida y segura</div>' + '</div>';
    let pagesHtml = '';
    for (let p = 0; p < totalPages; p++) {
      const start = p * perPage;
      const end = Math.min(start + perPage, safeQty);
      let pageTiles = '';
      for (let i = start; i < end; i++) {
        pageTiles += tileHtml;
      }
      pagesHtml += '<div class="page">' + pageTiles + '</div>';
    }
    const styleTag = '<' + 'style>' + css + '<' + '/style>';
    const html = '<!DOCTYPE html><html><head><title>Hoja QR - ' + restaurant.name + '</title>' + styleTag + '</head><body>' + '<div class="toolbar">' + '<button onclick="window.print()">Imprimir / Guardar PDF</button>' + '<span class="info">' + safeQty + ' QR · ' + totalPages + ' página' + (totalPages > 1 ? 's' : '') + ' · ' + perPage + ' por página</span>' + '</div>' + pagesHtml + '</body></html>';
    printWindow.document.write(html);
    printWindow.document.close();
  };
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4994">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-4995">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-4996" />
        </div>
      </AdminLayout>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4997">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-4998">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-4999">
          <div className="flex items-center gap-4" data-id="element-5000">
            <Link to={`/admin/${restaurant.slug}/dashboard`} className="p-2 -ml-2 text-white/50 hover:text-white rounded-full hover:bg-white/10 transition-colors" data-id="element-5001">
              <ArrowLeft className="w-6 h-6" data-id="element-5002" />
            </Link>
            <div data-id="element-5003">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight" data-id="element-5004">
                Mi menú digital QR
              </h1>
              <p className="text-white/50 text-sm mt-1" data-id="element-5005">
                Compartí este QR para que tus clientes vean tu menú online
              </p>
            </div>
          </div>
        </div>

        {/* URL bar */}
        <div className="mb-6 flex flex-col sm:flex-row items-stretch gap-3 bg-[#141414] border border-white/10 rounded-2xl p-3" data-id="element-5006">
          <div className="flex-1 min-w-0 flex items-center gap-2 px-3" data-id="element-5007">
            <ExternalLink className="w-4 h-4 text-white/40 shrink-0" data-id="element-5008" />
            <a href={menuUrl} target="_blank" rel="noopener noreferrer" className="truncate text-sm text-white/80 hover:text-white" data-id="element-5009">
              {menuUrl}
            </a>
          </div>
          <button onClick={handleCopy} className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors" data-id="element-5010">
            {copied ? <>
                <Check className="w-4 h-4" data-id="element-5011" />
                Copiado
              </> : <>
                <Copy className="w-4 h-4" data-id="element-5012" />
                Copiar enlace
              </>}
          </button>
        </div>

        {/* QR Card */}
        <div className="flex justify-center" data-id="element-5013">
          {loading ? <Skeleton className="h-[560px] w-full max-w-md rounded-2xl bg-[#141414]" data-id="element-5014" /> : <div className="bg-white rounded-2xl p-8 flex flex-col items-center text-center shadow-xl w-full max-w-md" data-id="element-5015">
              {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-20 h-20 rounded-xl object-cover mb-4 shadow-sm border border-gray-100" data-id="element-5016" /> : <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center mb-4" data-id="element-5017">
                  <Store className="w-10 h-10 text-gray-400" data-id="element-5018" />
                </div>}
              <h3 className="text-gray-500 font-bold text-sm uppercase tracking-wider mb-1" data-id="element-5019">
                {restaurant.name}
              </h3>
              <h2 className="text-3xl font-black text-gray-900 mb-6" data-id="element-5020">
                Nuestro menú digital
              </h2>

              <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 mb-6" data-id="element-5021">
                <img src={qrDataUrl} alt="QR Menú" className="w-full max-w-[280px] h-auto" data-id="element-5022" />
              </div>

              <p className="text-lg font-bold text-gray-800" data-id="element-5023">
                Escaneá para ver el menú
              </p>
              <p className="text-xs font-medium text-gray-400 mt-2 mb-6" data-id="element-5024">
                Autoatención rápida y segura
              </p>

              <div className="w-full grid grid-cols-2 gap-3 pt-4 border-t border-gray-100" data-id="element-5025">
                <Button variant="secondary" onClick={handleDownload} className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 border-0" data-id="element-5026">
                  <Download className="w-4 h-4 mr-2" data-id="element-5027" />
                  PNG
                </Button>
                <Button variant="secondary" onClick={handlePrint} className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 border-0" data-id="element-5028">
                  <Printer className="w-4 h-4 mr-2" data-id="element-5029" />
                  Imprimir
                </Button>
              </div>
            </div>}
        </div>

        <div className="mt-10 max-w-2xl mx-auto bg-[#141414] border border-white/10 rounded-2xl p-6" data-id="element-5030">
          <div className="flex items-center gap-3 mb-1" data-id="element-5031">
            <Layers className="w-5 h-5 text-white/60" data-id="element-5032" />
            <h2 className="text-lg font-bold text-white" data-id="element-5033">Hoja para imprimir</h2>
          </div>
          <p className="text-white/50 text-sm mb-5" data-id="element-5034">
            Generá una hoja con varios QR listos para recortar y pegar en mesas,
            vitrinas o volantes.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5" data-id="element-5035">
            <div data-id="element-5036">
              <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2" data-id="element-5037">
                Cantidad total
              </label>
              <input type="number" min={1} max={500} value={quantity} onChange={e => setQuantity(Number(e.target.value))} className="w-full px-4 py-3 rounded-xl bg-[#0B0B0B] border border-white/10 text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-white/30" data-id="element-5038" />
              <p className="text-xs text-white/40 mt-1" data-id="element-5039">Máximo 500</p>
            </div>
            <div data-id="element-5040">
              <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2" data-id="element-5041">
                QR por página
              </label>
              <select value={perPage} onChange={e => setPerPage(Number(e.target.value))} className="w-full px-4 py-3 rounded-xl bg-[#0B0B0B] border border-white/10 text-white text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-white/30 appearance-none" data-id="element-5042">
                <option value={6} data-id="element-5043">6 (grandes)</option>
                <option value={12} data-id="element-5044">12 (medianos)</option>
                <option value={24} data-id="element-5045">24 (pequeños)</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 px-4 py-3 rounded-xl bg-[#0B0B0B]/60 border border-white/5" data-id="element-5046">
            <div className="text-sm text-white/70" data-id="element-5047">
              Necesitarás aproximadamente{' '}
              <span className="font-bold text-white" data-id="element-5048">
                {Math.ceil(Math.max(1, Math.min(500, quantity || 1)) / perPage)}
              </span>{' '}
              página(s) en A4
            </div>
          </div>

          <Button onClick={handlePrintSheet} disabled={loading || !qrDataUrl} className="w-full bg-white text-gray-900 hover:bg-gray-100 font-bold" data-id="element-5049">
            <FileText className="w-5 h-5 mr-2" data-id="element-5050" />
            Generar hoja PDF
          </Button>
          <p className="text-xs text-white/40 text-center mt-3" data-id="element-5051">
            Se abrirá una ventana de impresión. Elegí "Guardar como PDF" o
            imprimí directamente.
          </p>
        </div>
      </div>
    </AdminLayout>;
}